import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, DeleteOutline, Description } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDateTime } from '@campfire/hot-date';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useDownloadCertificate } from '../../../../../common/certificates/useDownloadCertificate';
import { CertificateDownloadButton } from '../../../../../common/certificates/CertificateDownloadButton';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { GET_VOLUNTEER_CERTIFICATES } from './volunteer-certificates-section.gql';
import {
  GetVolunteerCertificates,
  GetVolunteerCertificatesVariables,
  GetVolunteerCertificates_vm_volunteer_certificates as Certificate,
} from './__generated__/GetVolunteerCertificates';
import { IssueCertificateDialog } from './IssueCertificateDialog';

const useStyles = makeStyles(() =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 0',
      '& + &': { borderTop: '1px solid #EFEFEF' },
    },
    icon: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      background: '#F2F2F2',
      color: '#646464',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 'none',
    },
    body: { flex: 1, minWidth: 0 },
    title: { fontSize: '14px', fontWeight: 600, color: '#333' },
    meta: { fontSize: '12px', color: '#767676', marginTop: '2px' },
    empty: { fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' },
  })
);

interface Props {
  volunteerId: string;
  canManage: boolean;
}

const describe = (certificate: Certificate) => {
  const parts = [
    ...(certificate.awardedFor ? [certificate.templateName] : []),
    `Issued ${unpackToDateTime(certificate.issuedAt).toFormat('d MMM yyyy')}`,
  ];
  const by = certificate.issuedBy?.profile;
  if (certificate.awardedAchievement) parts.push('From an achievement');
  else if (by) parts.push(`By ${by.preferredName} ${by.lastName}`);
  return parts.join(' · ');
};

export const VolunteerCertificatesSection = ({ volunteerId, canManage }: Props) => {
  const classes = useStyles();
  const {
    volunteerNameOptions: { preferredName },
  } = useVolunteerCommonProfileContext();
  const { setSnackbar } = useSnackbar();
  const { data, refetch } = useCampfireQuery<GetVolunteerCertificates, GetVolunteerCertificatesVariables>(
    GET_VOLUNTEER_CERTIFICATES,
    { options: { variables: { volunteerId } } }
  );
  const issueFetch = useEndpointFetch<{
    volunteerIds: string[];
    certificateTemplateId: string;
    awardedFor: string | null;
  }>('/vm/admin-console/certificate/issue');
  const removeFetch = useEndpointFetch<{ volunteerCertificateId: string }>('/vm/admin-console/certificate/remove');
  const { download } = useDownloadCertificate();
  const [issueOpen, setIssueOpen] = useState(false);
  const [removing, setRemoving] = useState<Certificate>();
  const [downloadingId, setDownloadingId] = useState<string>();

  const certificates = data?.vm.volunteer?.certificates ?? [];

  const issue = (certificateTemplateId: string, awardedFor: string) =>
    issueFetch
      .run({ volunteerIds: [volunteerId], certificateTemplateId, awardedFor: awardedFor.trim() || null })
      .then((response) => {
        if (!response.ok) throw new Error(response.data?.message);
        setSnackbar({ open: true, variant: 'success', message: 'Certificate issued' });
        setIssueOpen(false);
        if (refetch) setTimeout(refetch, 0);
      })
      .catch((e) =>
        setSnackbar({ open: true, variant: 'error', message: e?.message || 'Unable to issue certificate' })
      );

  const onRemoveConfirm = () => {
    if (!removing) return;
    const { volunteerCertificateId } = removing;
    setRemoving(undefined);
    removeFetch
      .run({ volunteerCertificateId })
      .then((response) => {
        if (!response.ok) throw new Error(response.data?.message);
        setSnackbar({ open: true, variant: 'success', message: 'Certificate removed' });
        if (refetch) setTimeout(refetch, 0);
      })
      .catch((e) =>
        setSnackbar({ open: true, variant: 'error', message: e?.message || 'Unable to remove certificate' })
      );
  };

  const onDownload = (volunteerCertificateId: string) => {
    setDownloadingId(volunteerCertificateId);
    download(volunteerCertificateId).finally(() => setDownloadingId(undefined));
  };

  return (
    <Box textAlign='left'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Box>
          <Typography
            style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
          >
            <Description style={{ fontSize: '14px', paddingRight: '5px' }} /> Certificates
          </Typography>
          <Typography variant='body2' style={{ color: '#464646' }} align='left'>
            Certificates {preferredName} has been issued
          </Typography>
        </Box>
        {canManage && (
          <TabletButton
            size='small'
            variant='contained'
            color='primary'
            onClick={() => setIssueOpen(true)}
            startIcon={<Add />}
            style={{
              height: 36,
              border: '1px solid transparent',
              boxSizing: 'border-box',
              borderRadius: '4px',
              padding: '6px 12px',
              marginRight: '10px',
              color: '#fff',
            }}
          >
            Add
          </TabletButton>
        )}
      </Box>
      <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />

      {certificates.map((certificate) => (
        <Box key={certificate.volunteerCertificateId} className={classes.row}>
          <Box className={classes.icon}>
            <Description fontSize='small' />
          </Box>
          <Box className={classes.body}>
            <Typography className={classes.title}>{certificate.awardedFor || certificate.templateName}</Typography>
            <Typography className={classes.meta}>{describe(certificate)}</Typography>
          </Box>
          <CertificateDownloadButton
            disabled={downloadingId === certificate.volunteerCertificateId}
            onClick={() => onDownload(certificate.volunteerCertificateId)}
          />
          {canManage && !certificate.awardedAchievement && (
            <Tooltip title='Remove'>
              <IconButton size='small' aria-label='Remove certificate' onClick={() => setRemoving(certificate)}>
                <DeleteOutline fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {data && certificates.length === 0 && (
        <Typography variant='body2' className={classes.empty}>
          No certificates issued yet
        </Typography>
      )}

      <ConfirmationDialog
        title='Remove certificate'
        body={`Remove "${removing?.awardedFor ||
          removing?.templateName ||
          ''}" from ${preferredName}'s certificates? They will no longer be able to download it.`}
        open={Boolean(removing)}
        closeActionText='Cancel'
        confirmActionText='Remove'
        handleCloseClick={() => setRemoving(undefined)}
        handleConfirmClick={onRemoveConfirm}
      />

      {canManage && (
        <IssueCertificateDialog
          open={issueOpen}
          volunteerName={preferredName}
          templates={data?.vm.certificateTemplates ?? []}
          saving={issueFetch.isLoading}
          onClose={() => setIssueOpen(false)}
          onIssue={issue}
        />
      )}
    </Box>
  );
};
